/* Hero split slideshow (sections/hero-split.liquid).
   Crossfades between slides (the CSS does the fading; this toggles .is-active), keeps the "01 / 03" counter current,
   marks hidden slides aria-hidden + inert, supports swipe, arrow keys on the controls, and the theme editor.
   Autoplay pauses while hovered (mouse), while keyboard focus or a focused link is inside, while the tab is hidden or
   the hero is off screen, while the section or one of its slides is selected in the editor, and when the visitor
   presses Pause. With reduced motion it starts paused.
   Images: only slide 1's image comes with the page. A later slide's image (data-hero-src / data-hero-srcset) loads
   PRELOAD_LEAD before autoplay shows it; autoplay then waits (up to WAIT_MAX) for it rather than fading in a blank
   slide. A slide shown by hand or in the editor loads at once, together with its neighbours; mouse hover, keyboard
   focus and a sideways swipe load the neighbours ahead of time (a vertical scroll over the hero loads nothing). */
if (!customElements.get('lmb-hero-split')) {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SWIPE_MIN = 40; // px of horizontal travel that counts as a swipe
  const PRELOAD_LEAD = 2000; // ms before an automatic slide change that the next slide's image starts loading
  const WAIT_MAX = 6000; // ms autoplay waits for a slow image before moving on anyway
  const SWIPE_INTENT = 10; // px of mostly horizontal travel that reveals a swipe

  /* Older Safari only has addListener / removeListener on media query lists. */
  const watchMotion = (handler, add) => {
    if (reducedMotion.addEventListener) reducedMotion[add ? 'addEventListener' : 'removeEventListener']('change', handler);
    else if (reducedMotion.addListener) reducedMotion[add ? 'addListener' : 'removeListener'](handler);
  };

  const isKeyboardFocus = (element) => {
    try {
      return element.matches(':focus-visible');
    } catch (error) {
      return true;
    }
  };

  class LmbHeroSplit extends HTMLElement {
    constructor() {
      super();
      this.timer = null;
      this.preloadTimer = null;
      /* Reasons the slideshow is paused. It plays only when this is empty. */
      this.pauses = new Set();
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.onMotionChange = this.onMotionChange.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
      this.onSectionDeselect = this.onSectionDeselect.bind(this);
    }

    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      if (this.slides.length < 2 || this.active) return;
      this.active = true;

      this.track = this.querySelector('[data-hero-track]');
      this.pauseButton = this.querySelector('[data-hero-pause]');
      this.currentLabel = this.querySelector('[data-hero-current]');
      this.autoplay = this.dataset.autoplay === 'true';
      this.delay = Math.max(3, parseFloat(this.dataset.speed) || 6) * 1000;
      if (typeof this.index !== 'number') {
        this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      }
      if (reducedMotion.matches) this.pauses.add('user');

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.addEventListener('touchmove', this.onTouchMove, { passive: true });
      this.addEventListener('touchend', this.onTouchEnd, { passive: true });
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      this.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      document.addEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      watchMotion(this.onMotionChange, true);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          const entry = entries[entries.length - 1];
          if (entry) this.setPause('offscreen', !entry.isIntersecting);
        });
        this.observer.observe(this);
      }

      this.pauses.delete('hidden');
      this.onVisibility();
      this.show(this.index);
    }

    /* Runs when the section is removed or re-rendered, and when the editor moves it (it then connects again). */
    disconnectedCallback() {
      if (!this.active) return;
      this.active = false;
      clearTimeout(this.timer);
      this.timer = null;
      clearTimeout(this.preloadTimer);
      this.preloadTimer = null;
      this.cancelWait();
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('pointerenter', this.onPointerEnter);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.removeEventListener('touchstart', this.onTouchStart);
      this.removeEventListener('touchmove', this.onTouchMove);
      this.removeEventListener('touchend', this.onTouchEnd);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
      watchMotion(this.onMotionChange, false);
      if (this.observer) this.observer.disconnect();
      this.observer = null;
      /* Transient reasons are re-read on reconnect; the visitor's own Pause and editor selections are kept. */
      ['hover', 'focus', 'offscreen', 'hidden'].forEach((reason) => this.pauses.delete(reason));
    }

    /* ---------- Slides ---------- */

    /* manual: shown by the visitor or the editor, so the next step either way is loaded ahead too. */
    show(index, manual = false) {
      const count = this.slides.length;
      this.index = ((index % count) + count) % count;
      this.loadSlide(this.index);
      if (manual) this.loadNeighbours();
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.toggleAttribute('inert', !active);
        if (active) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      if (this.currentLabel) this.currentLabel.textContent = String(this.index + 1).padStart(2, '0');
      this.schedule();
    }

    next(manual = false) {
      this.show(this.index + 1, manual);
    }

    previous(manual = false) {
      this.show(this.index - 1, manual);
    }

    /* Swaps in a slide's real image addresses (once). */
    loadSlide(index) {
      const count = this.slides.length;
      const slide = this.slides[((index % count) + count) % count];
      if (!slide || slide.dataset.heroLoaded) return;
      slide.dataset.heroLoaded = 'true';
      slide.querySelectorAll('[data-hero-srcset]').forEach((el) => {
        if (el.dataset.heroSizes) el.setAttribute('sizes', el.dataset.heroSizes);
        el.setAttribute('srcset', el.dataset.heroSrcset);
        el.removeAttribute('data-hero-srcset');
        el.removeAttribute('data-hero-sizes');
      });
      slide.querySelectorAll('img[data-hero-src]').forEach((img) => {
        img.loading = 'eager';
        img.src = img.dataset.heroSrc;
        img.removeAttribute('data-hero-src');
      });
    }

    loadNeighbours() {
      this.loadSlide(this.index + 1);
      this.loadSlide(this.index - 1);
    }

    slideImage(index) {
      const count = this.slides.length;
      const slide = this.slides[((index % count) + count) % count];
      return slide ? slide.querySelector('img.hero__img') : null;
    }

    imageReady(img) {
      const src = img.currentSrc || img.src || '';
      return img.complete && img.naturalWidth > 1 && !img.hasAttribute('data-hero-src') && !src.startsWith('data:');
    }

    /* Autoplay: move on once the next slide's image is there (or after WAIT_MAX), never onto a blank slide. */
    advanceWhenReady() {
      this.loadSlide(this.index + 1);
      const img = this.slideImage(this.index + 1);
      if (!img || this.imageReady(img)) {
        this.next();
        return;
      }
      const go = () => {
        this.cancelWait();
        if (this.playing) this.next();
      };
      img.addEventListener('load', go);
      img.addEventListener('error', go);
      this.waitTimer = setTimeout(go, WAIT_MAX);
      this.cancelWait = () => {
        img.removeEventListener('load', go);
        img.removeEventListener('error', go);
        clearTimeout(this.waitTimer);
        this.cancelWait = () => {};
      };
    }

    cancelWait() {}

    /* ---------- Autoplay ---------- */

    get playing() {
      return this.autoplay && this.active && this.pauses.size === 0;
    }

    schedule() {
      clearTimeout(this.timer);
      this.timer = null;
      clearTimeout(this.preloadTimer);
      this.preloadTimer = null;
      this.cancelWait();
      if (this.playing) {
        this.timer = setTimeout(() => this.advanceWhenReady(), this.delay);
        this.preloadTimer = setTimeout(() => this.loadSlide(this.index + 1), Math.max(0, this.delay - PRELOAD_LEAD));
      }
      // Announce slide changes only when they are not automatic.
      if (this.track) this.track.setAttribute('aria-live', this.playing ? 'off' : 'polite');
      this.updatePauseButton();
    }

    setPause(reason, paused) {
      if (paused === this.pauses.has(reason)) return;
      if (paused) this.pauses.add(reason);
      else this.pauses.delete(reason);
      this.schedule();
    }

    updatePauseButton() {
      if (!this.pauseButton) return;
      const userPaused = this.pauses.has('user');
      const text = userPaused ? this.dataset.labelPlay : this.dataset.labelPause;
      const label = userPaused ? this.dataset.labelPlayLong : this.dataset.labelPauseLong;
      if (text && this.pauseButton.textContent !== text) this.pauseButton.textContent = text;
      if (label) this.pauseButton.setAttribute('aria-label', label);
    }

    togglePlayback() {
      if (this.pauses.has('user')) {
        // The visitor asked to play: don't let the hover or focus that got them to the button hold it back.
        this.pauses.delete('user');
        this.pauses.delete('hover');
        this.pauses.delete('focus');
      } else {
        this.pauses.add('user');
      }
      this.schedule();
    }

    /* ---------- Events ---------- */

    onClick(event) {
      const control = event.target.closest('[data-hero-pause], [data-hero-prev], [data-hero-next]');
      if (!control || !this.contains(control)) return;
      if (control.hasAttribute('data-hero-pause')) this.togglePlayback();
      else if (control.hasAttribute('data-hero-prev')) this.previous(true);
      else this.next(true);
    }

    onKeydown(event) {
      if (!event.target.closest('.hero__controls')) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next(true);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.previous(true);
      }
    }

    onPointerEnter(event) {
      if (event.pointerType !== 'mouse') return;
      this.loadNeighbours();
      this.setPause('hover', true);
    }

    onPointerLeave(event) {
      if (event.pointerType === 'mouse') this.setPause('hover', false);
    }

    /* Keyboard focus anywhere, or focus on a link in a slide, holds the slide. A mouse click on Pause or the arrows
       only focuses the button, so it doesn't count: the hover pause already covers the pointer being there. */
    onFocusIn(event) {
      this.loadNeighbours();
      const onControl = event.target.closest && event.target.closest('.hero__controls');
      if (onControl && !isKeyboardFocus(event.target)) return;
      this.setPause('focus', true);
    }

    onFocusOut(event) {
      if (!this.contains(event.relatedTarget)) this.setPause('focus', false);
    }

    onTouchStart(event) {
      const touch = event.touches[0];
      this.touchStart = event.touches.length === 1 ? { x: touch.clientX, y: touch.clientY } : null;
      this.swipeLoaded = false;
    }

    /* A sideways swipe loads the slide it is heading to before the finger lifts; a vertical scroll loads nothing. */
    onTouchMove(event) {
      if (!this.touchStart || this.swipeLoaded) return;
      const touch = event.touches[0];
      const dx = touch.clientX - this.touchStart.x;
      const dy = touch.clientY - this.touchStart.y;
      if (Math.abs(dx) < SWIPE_INTENT || Math.abs(dx) < Math.abs(dy)) return;
      this.swipeLoaded = true;
      this.loadSlide(this.index + (dx < 0 ? 1 : -1));
    }

    onTouchEnd(event) {
      if (!this.touchStart) return;
      const touch = event.changedTouches[0];
      const dx = touch.clientX - this.touchStart.x;
      const dy = touch.clientY - this.touchStart.y;
      this.touchStart = null;
      if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      if (dx < 0) this.next(true);
      else this.previous(true);
    }

    onVisibility() {
      this.setPause('hidden', document.hidden);
    }

    onMotionChange() {
      if (reducedMotion.matches) this.setPause('user', true);
    }

    onBlockSelect(event) {
      const index = this.slides.indexOf(event.target);
      if (index === -1) return;
      this.pauses.add('editor-block');
      this.show(index, true);
    }

    onBlockDeselect() {
      this.setPause('editor-block', false);
    }

    onSectionSelect(event) {
      if (event.detail && event.detail.sectionId === this.dataset.sectionId) this.setPause('editor-section', true);
    }

    onSectionDeselect(event) {
      if (event.detail && event.detail.sectionId === this.dataset.sectionId) this.setPause('editor-section', false);
    }
  }

  customElements.define('lmb-hero-split', LmbHeroSplit);
}
